import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet
} from '@react-pdf/renderer';

// Simple styles using only built-in fonts
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontSize: 12,
  },
  title: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: 'center',
    color: '#057c69',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 10,
    color: '#057c69',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: 120,
    fontSize: 12,
  },
  value: {
    fontSize: 12,
    flex: 1,
  },
  table: {
    marginBottom: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    padding: 8,
    borderBottom: 1,
    borderBottomColor: '#CCCCCC',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: 0.5,
    borderBottomColor: '#E0E0E0',
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
  },
  tableCellHeader: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#666666',
  },
});

interface SimplePDFReportProps {
  data: any;
}

const SimplePDFReport: React.FC<SimplePDFReportProps> = ({ data }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text style={styles.title}>FlahaFAST Nutrient Recipe Report</Text>
        
        {/* Basic Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Recipe Information</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Recipe Name:</Text>
            <Text style={styles.value}>{data.formulation?.name || 'Custom Recipe'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Water Source:</Text>
            <Text style={styles.value}>{data.waterProfile?.name || 'Unknown'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>{new Date(data.metadata?.createdDate || Date.now()).toLocaleDateString()}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Stock Volume:</Text>
            <Text style={styles.value}>{data.metadata?.stockVolumePerTank || 10} L per tank</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Dilution Ratio:</Text>
            <Text style={styles.value}>1:{data.metadata?.dilutionRatio || 100}</Text>
          </View>
        </View>

        {/* Salt Amounts */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Salt Requirements</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCellHeader}>Salt Name</Text>
              <Text style={styles.tableCellHeader}>Amount (g)</Text>
              <Text style={styles.tableCellHeader}>Tank</Text>
            </View>
            {data.saltAmounts && data.saltAmounts.length > 0 ? (
              data.saltAmounts.map((salt: any, index: number) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{salt.name || 'Unknown Salt'}</Text>
                  <Text style={styles.tableCell}>{(salt.amount || 0).toFixed(2)}</Text>
                  <Text style={styles.tableCell}>{salt.tankAssignment || 'A'}</Text>
                </View>
              ))
            ) : (
              <View style={styles.tableRow}>
                <Text style={styles.tableCell}>No salt data available</Text>
                <Text style={styles.tableCell}>-</Text>
                <Text style={styles.tableCell}>-</Text>
              </View>
            )}
          </View>
        </View>

        {/* Solution Properties */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Solution Properties</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Final EC:</Text>
            <Text style={styles.value}>{data.calculatedEC?.toFixed(2) || '2.0'} dS/m</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Final pH:</Text>
            <Text style={styles.value}>{data.solutionProperties?.finalPH?.toFixed(1) || '6.0'}</Text>
          </View>
        </View>

        {/* Instructions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Preparation Instructions</Text>
          <Text style={styles.value}>1. Prepare Tank A: Dissolve macronutrient salts in {data.metadata?.stockVolumePerTank || 10}L water</Text>
          <Text style={styles.value}>2. Prepare Tank B: Dissolve micronutrient salts in {data.metadata?.stockVolumePerTank || 10}L water</Text>
          <Text style={styles.value}>3. Mix solutions at 1:{data.metadata?.dilutionRatio || 100} ratio for irrigation</Text>
          <Text style={styles.value}>4. Monitor EC and pH, adjust as needed</Text>
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Generated by FlahaFAST DSS v1.2 | {new Date().toLocaleDateString()}
        </Text>
      </Page>
    </Document>
  );
};

export default SimplePDFReport;
